// Navaid symbology rendered as small SVG map images. Convention
// follows the FAA Sectional Chart Users Guide:
//
//   VOR        Hexagon with center dot. The hexagon represents the
//              compass rose surrounding a VOR station.
//   VOR/DME    Same hexagon, enclosed by a square (DME = ranging).
//   VORTAC     Hexagon with three small "pegs" (TACAN antennas) at
//              alternating vertices. Combined VOR + TACAN.
//   TACAN      Same pegged hexagon as VORTAC but typically smaller;
//              we draw it identical to VORTAC because pilot
//              recognition matters more than chart-purity here.
//   NDB        Concentric dotted ring (the "doughnut" symbol).
//   DME        Plain square (rare standalone, mostly co-located).
//   VOT        VOR test \u2014 render same as VOR; some charts use a
//              dashed hexagon, but at small EFB sizes the dashes
//              read as noise.
//
// Color: dark cyan/teal (\\#22d3ee with darker stroke) so the icons
// stay readable on both the cream Sectional and a black satellite
// basemap. Black halo stroke gives contrast.

import type { Map as MlMap, ExpressionSpecification } from 'maplibre-gl';
import { ensureSvgImages } from './iconRegistry';

// Bumped up from 28 to 36 + dark backing fill so the icons dominate
// the Sectional chart's baked-in VOR compass rose at the same lat/lon.
const SIZE = 36;
const CX = SIZE / 2;
const CY = SIZE / 2;
const R  = 10;

const FILL = '#0b0d0fcc';   // dark backing so hex reads on top of chart rose
const STROKE = '#22d3ee';   // bright cyan \u2014 high contrast on chart + sat
const HALO   = '#0b0d0f';   // dark halo for legibility
const DOT    = '#22d3ee';

/** Hexagon vertices oriented FLAT-TOP (a.k.a. point-right), which is
 *  the FAA Sectional convention: two horizontal edges, two vertical
 *  bevels. Returns six points as "x,y x,y ..." for a polygon. */
function hexPoints(cx: number, cy: number, r: number): string {
  // 0 deg = right; flat-top = vertices at 0, 60, 120, 180, 240, 300
  const pts: string[] = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 180) * (i * 60);
    const x = cx + r * Math.cos(a);
    const y = cy + r * Math.sin(a);
    pts.push(`${x.toFixed(2)},${y.toFixed(2)}`);
  }
  return pts.join(' ');
}

function svgVor(): string {
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}">
      <polygon points="${hexPoints(CX, CY, R)}"
               fill="${FILL}" stroke="${HALO}" stroke-width="3.0" stroke-linejoin="round"/>
      <polygon points="${hexPoints(CX, CY, R)}"
               fill="${FILL}" stroke="${STROKE}" stroke-width="1.6" stroke-linejoin="round"/>
      <circle cx="${CX}" cy="${CY}" r="1.4" fill="${DOT}" stroke="${HALO}" stroke-width="0.5"/>
    </svg>
  `;
}

function svgVorDme(): string {
  // VOR rose enclosed by a DME-ranging square.
  const sqHalf = R + 2;
  const x0 = CX - sqHalf, y0 = CY - sqHalf;
  const sqW = sqHalf * 2;
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}">
      <rect x="${x0}" y="${y0}" width="${sqW}" height="${sqW}"
            fill="${FILL}" stroke="${HALO}" stroke-width="3.0"/>
      <rect x="${x0}" y="${y0}" width="${sqW}" height="${sqW}"
            fill="${FILL}" stroke="${STROKE}" stroke-width="1.6"/>
      <polygon points="${hexPoints(CX, CY, R - 0.5)}"
               fill="${FILL}" stroke="${HALO}" stroke-width="2.2" stroke-linejoin="round"/>
      <polygon points="${hexPoints(CX, CY, R - 0.5)}"
               fill="${FILL}" stroke="${STROKE}" stroke-width="1.4" stroke-linejoin="round"/>
      <circle cx="${CX}" cy="${CY}" r="1.3" fill="${DOT}"/>
    </svg>
  `;
}

function svgVortac(): string {
  // Hexagon with three pegs (TACAN antenna triplet) at alternating
  // vertices. Pegs project outward from vertices 0, 120, 240.
  const pegLen = 4.5;
  const pegWid = 2.4;
  const pegs: string[] = [];
  for (const i of [0, 2, 4]) {
    const a = (Math.PI / 180) * (i * 60);
    const vx = CX + R * Math.cos(a);
    const vy = CY + R * Math.sin(a);
    const ox = vx + pegLen * Math.cos(a);
    const oy = vy + pegLen * Math.sin(a);
    // Render as a thick line segment with rounded caps so it reads
    // as a chunky peg even when small.
    pegs.push(`<line x1="${vx.toFixed(2)}" y1="${vy.toFixed(2)}" x2="${ox.toFixed(2)}" y2="${oy.toFixed(2)}" stroke="${HALO}" stroke-width="${pegWid + 1.2}" stroke-linecap="round"/>`);
    pegs.push(`<line x1="${vx.toFixed(2)}" y1="${vy.toFixed(2)}" x2="${ox.toFixed(2)}" y2="${oy.toFixed(2)}" stroke="${STROKE}" stroke-width="${pegWid}" stroke-linecap="round"/>`);
  }
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}">
      ${pegs.join('')}
      <polygon points="${hexPoints(CX, CY, R)}"
               fill="${FILL}" stroke="${HALO}" stroke-width="3.0" stroke-linejoin="round"/>
      <polygon points="${hexPoints(CX, CY, R)}"
               fill="${FILL}" stroke="${STROKE}" stroke-width="1.6" stroke-linejoin="round"/>
      <circle cx="${CX}" cy="${CY}" r="1.4" fill="${DOT}" stroke="${HALO}" stroke-width="0.5"/>
    </svg>
  `;
}

function svgNdb(): string {
  // Concentric dotted ring \u2014 small dots arrayed around a center point.
  const innerR = 3.5;
  const outerR = 8;
  const dots: string[] = [];
  // Inner ring of 8 small dots.
  for (let i = 0; i < 8; i++) {
    const a = (Math.PI / 180) * (i * 45);
    dots.push(`<circle cx="${(CX + innerR * Math.cos(a)).toFixed(2)}" cy="${(CY + innerR * Math.sin(a)).toFixed(2)}" r="0.9" fill="${HALO}"/>`);
    dots.push(`<circle cx="${(CX + innerR * Math.cos(a)).toFixed(2)}" cy="${(CY + innerR * Math.sin(a)).toFixed(2)}" r="0.6" fill="${STROKE}"/>`);
  }
  // Outer ring of 12 small dots, slightly smaller \u2014 the "doughnut"
  // hint that distinguishes NDB from VOR.
  for (let i = 0; i < 12; i++) {
    const a = (Math.PI / 180) * (i * 30 + 15);
    dots.push(`<circle cx="${(CX + outerR * Math.cos(a)).toFixed(2)}" cy="${(CY + outerR * Math.sin(a)).toFixed(2)}" r="0.7" fill="${HALO}"/>`);
    dots.push(`<circle cx="${(CX + outerR * Math.cos(a)).toFixed(2)}" cy="${(CY + outerR * Math.sin(a)).toFixed(2)}" r="0.5" fill="${STROKE}"/>`);
  }
  // Center dot.
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}">
      ${dots.join('')}
      <circle cx="${CX}" cy="${CY}" r="1.4" fill="${DOT}" stroke="${HALO}" stroke-width="0.5"/>
    </svg>
  `;
}

function svgDme(): string {
  // Plain square \u2014 standalone DME (rare).
  const half = R - 1;
  const x0 = CX - half, y0 = CY - half;
  const w = half * 2;
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}">
      <rect x="${x0}" y="${y0}" width="${w}" height="${w}"
            fill="${FILL}" stroke="${HALO}" stroke-width="3.0"/>
      <rect x="${x0}" y="${y0}" width="${w}" height="${w}"
            fill="${FILL}" stroke="${STROKE}" stroke-width="1.6"/>
      <circle cx="${CX}" cy="${CY}" r="1.3" fill="${DOT}"/>
    </svg>
  `;
}

const SYMBOLS: Array<{ id: string; svg: string }> = [
  { id: 'navaid-vor',     svg: svgVor() },
  { id: 'navaid-vor-dme', svg: svgVorDme() },
  { id: 'navaid-vortac',  svg: svgVortac() },
  { id: 'navaid-tacan',   svg: svgVortac() },  // visually same as VORTAC
  { id: 'navaid-ndb',     svg: svgNdb() },
  { id: 'navaid-dme',     svg: svgDme() },
  { id: 'navaid-vot',     svg: svgVor() },     // VOT \u2192 same as VOR
];

export function ensureNavaidIcons(map: MlMap): Promise<void> {
  return ensureSvgImages(map, SYMBOLS, {
    defaultSize: SIZE,
    logTag: '[navaidIcons]',
  });
}

/** Inline SVG strings for use in static UI (legends, key cards).
 *  These are the same symbols the map uses, just embedded in the
 *  document directly so the legend renders even when the map hasn't
 *  finished registering its image atlas. */
export const NAVAID_LEGEND_SVGS = {
  VOR:     svgVor(),
  VORDME:  svgVorDme(),
  VORTAC:  svgVortac(),
  NDB:     svgNdb(),
  DME:     svgDme(),
};

/** Map a feature's `type` to one of the registered icon ids.
 *  Order matters \u2014 first match wins. */
export const NAVAID_ICON_EXPRESSION: ExpressionSpecification = [
  'match',
  ['get', 'type'],
  'VOR',     'navaid-vor',
  'VOT',     'navaid-vot',
  'VOR/DME', 'navaid-vor-dme',
  'VORTAC',  'navaid-vortac',
  'TACAN',   'navaid-tacan',
  'NDB',     'navaid-ndb',
  'DME',     'navaid-dme',
  /* default */ 'navaid-vor',
] as unknown as ExpressionSpecification;
